import { Icon } from "@mdi/react";
import * as Sentry from "@sentry/react";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CellClickedEventArgs,
  DataEditor,
  GridCell,
  GridColumn,
  Item
} from "@glideapps/glide-data-grid";
import { ErrorOutline } from "@material-ui/icons";
import { mdiDownload, mdiFileUpload } from "@mdi/js";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Steps,
  Tooltip
} from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { UploadFile } from "antd/lib/upload/interface";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import { Subscription } from "rxjs";
import { CustomFieldPayload } from "store/features";
import { getNormalizationItems } from "store/features/normalizeBy/normalizeBySlice";
import { RootState } from "store/rootReducer";
import styled, { createGlobalStyle, css } from "styled-components";
import { extractAxiosErrorMessage } from "utils/errorUtil";

import { ColumnModel, UploadExcelResult } from "api/data";

import { EntityKind } from "models/entityKind";

import { ErrorBoundary } from "components/base";
import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { IconSpinner } from "components/icons";
import { useAutoGroupByAfterImport } from "components/user-defined-columns/hooks/useAutoGroupByAfterImport";
import {
  collectExistingColumnNames,
  countDuplicateGroups,
  deriveUnnamedColumnItems,
  findConflictingNameIndices,
  getColumnNameError,
  renameWorkbookHeaders,
  stripDedupSuffix
} from "components/user-defined-columns/utils/columnNamingUtils";
import {
  countErrorCells,
  errorDataRowIndices,
  getData,
  getRawData,
  getRawPreviewColumnOrder,
  getStyledColumns
} from "components/user-defined-columns/utils/userDefinedColumnUtils";

import { columnUploadedSignalAtom } from "./atoms/fieldGroupAtoms";
import ImportIssuesPanel from "./components/ImportIssuesPanel";
import { GLIDE_GRID_THEME } from "./constants/glideGridTheme";
import { useDownloadTemplateMutation } from "./mutations/useDownloadTemplateMutation";
import { usePostUserDefinedColumnMutation } from "./mutations/useUserDefinedColumnPostMutation";
import { useUploadCustomColumnFileMutation } from "./mutations/useUserDefinedColumnUploadMutation";

export const MAX_PREVIEW_ROWS = 15;
// Every non-key preview column is the same width in every state (named, headerless,
// duplicate) so the grid reads uniformly. The key column stays wider to fit long IDs.
const PREVIEW_COLUMN_WIDTH = 130;
const KEY_COLUMN_WIDTH = 170;

// Shown on the key-header and column-select controls while they are locked behind the
// headerless-column naming step, so hovering explains why they can't be changed yet.
const NAMING_LOCK_HINT = "Resolve the highlighted column headers first.";

// Constant across every step (and the naming step) so the modal never resizes mid-session; caps
// fit a 1440x900 viewport without page/modal-body scrolling.
const MODAL_WIDTH = "min(1180px, 90vw)";

// Scoped through wrapClassName (antd Modal portals outside this component's own DOM, so a
// styled-components class here would never reach it) — constant modal body height leaves
// room for the header/footer at a 900px-tall viewport.
const ModalGlobalStyle = createGlobalStyle`
  .user-defined-modal-root .ant-modal-body {
    height: min(calc(100vh - 200px), 820px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

export function getPreviewHeaderText(
  effectiveNumRows: number,
  totalRows: number
): string {
  return `Preview ${effectiveNumRows}/${totalRows}`;
}

let sub: Subscription = null;

const initialColumns: GridColumn[] = [
  { title: "Entity", width: 170 },
  { title: "Value", width: 200 }
];
export const defaultUwiHeaders = ["UWI", "Formatted UWI Full", "Formatted UWI Short"];

export default function UserDefinedColumnEditWizard() {
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const [editsConfirmed, setEditsConfirmed] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [columns, setColumns] = useState(initialColumns);
  // `columns` represents the current set of columns displayed in the table.
  // It is updated when a file is uploaded or when edits are confirmed.
  const [entityKind, setEntityKind] = useState(EntityKind.Well);
  const [currentStep, setCurrentStep] = useState(0);
  // Re-derived (current date) on every open/reset; used as the visible placeholder and as
  // the applied group name whenever the input is cleared.
  const groupNameDefaultRef = useRef(dayjs().format("YYYY-MM-DD"));
  const [groupName, setGroupName] = useState(groupNameDefaultRef.current);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [tempColumns, setTempColumns] = useState<ColumnModel[]>([]);
  const [showInvalidUwisOnly, setShowInvalidUwisOnly] = useState(false);
  // Raw-preview row filter: when on, the preview shows only rows that contain a genuine
  // Excel error cell (mirrors the invalid-key row filter). Both filters compose as a union.
  const [showErrorRowsOnly, setShowErrorRowsOnly] = useState(false);
  // When on, the raw preview shows the original wire value of each error cell instead of the
  // normalized "n/a" (the error cells stay tinted red either way).
  const [showActualValues, setShowActualValues] = useState(false);
  // In-place naming overlay for the headerless-column naming step: which unnamed column is
  // being edited and where its header cell sits within the grid container. Mirrored in a
  // ref so the modal's capture-phase Escape listener can cancel the overlay instead of
  // closing the wizard.
  const [headerNameEditing, setHeaderNameEditing] = useState<
    Nullable<{
      rawIndex: number;
      initialDraft: string;
      bounds: { x: number; y: number; width: number; height: number };
    }>
  >(null);
  const headerNameEditingRef = useRef<typeof headerNameEditing>(null);
  // Last raw-grid visible region (top-left cell), used to tell a real scroll from a mere
  // resize so typing in the rename overlay never dismisses it.
  const lastVisibleRegionRef = useRef<Nullable<{ x: number; y: number }>>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // The backend's original columnsWithErrors (raw header names). confirmColumnEdits rewrites
  // dataRef.current.columnsWithErrors to the user's edited names for Review header tinting,
  // so the error count keeps this raw set to stay matched to rawDataColumns.headerName.
  const uploadErrorColumnsRef = useRef<string[]>([]);
  // The kept-column names frozen when leaving the upload step, so the error count on later
  // steps (where column names are edited) still reflects the user's de-selection.
  const includedColumnNamesRef = useRef<Nullable<Set<string>>>(null);
  const [availableKeyHeaders, setAvailableKeyHeaders] = useState<string[]>([]);
  // Draft names typed into the headerless-column naming step, keyed by rawDataColumns index.
  const [columnNameDrafts, setColumnNameDrafts] = useState<Record<number, string>>({});
  // false only while the LATEST response has unnamedColumns and the user has not proceeded.
  const [columnNamingResolved, setColumnNamingResolved] = useState(true);
  // Per-session preference, always re-checked on wizard open — not persisted.
  const [autoGroupByChecked, setAutoGroupByChecked] = useState(true);

  // `tempColumns` is used to temporarily store column edits during the "Edit Fields" step.
  // Changes here are not applied to `columns` until the user confirms the edits.
  const dataRef: MutableRefObject<UploadExcelResult> = useRef(null);
  // `dataRef.current.columns` contains the raw column data from the uploaded file.
  // It is used as the source of truth for the uploaded data and is not directly modified.
  const keyHeaderRef = useRef("Entity");
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );

  const isOrgRef = useRef(false);
  const projectIdRef = useRef(null);
  const uploadCallbackRef = useRef<{
    file?: File;
    onSuccess: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => void;
  }>({
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSuccess: () => {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  });
  // Set while a naming-triggered rename re-upload is in flight; the next upload response
  // auto-resolves the naming step instead of being treated as a fresh response to re-evaluate.
  // Cleared on both the success and the failure path of that re-upload so a later,
  // unrelated response is never mistaken for a rename re-upload result.
  const renameReuploadPendingRef = useRef(false);
  // Set when an internal refresh re-uploads the file whose columns were already named (Previous from Edit
  // Fields): the user's naming decision stands, so the response must not reopen the naming step.
  const namingReevalSuppressedRef = useRef(false);
  // The names entered on the most recent naming-triggered rename re-upload, kept so the
  // response can be checked for whether each entered name actually landed.
  const renameReuploadNamesRef = useRef<Record<number, string> | null>(null);

  const dispatch = useDispatch();
  const { getCategoryListWithUnit } = useGetCategoryList();
  const setColumnUploadedSignal = useSetAtom(columnUploadedSignalAtom);
  const { setPendingTarget } = useAutoGroupByAfterImport();

  const postMutation = usePostUserDefinedColumnMutation(
    entityKind,
    isOrgRef.current,
    projectIdRef.current,
    () => {
      // Capture the submitted columns before dataRef is nulled: best-effort, never blocks
      // or fails the import.
      if (autoGroupByChecked && dataRef.current) {
        const submittedKeyHeader = dataRef.current.uwiHeaderName || keyHeaderRef.current;
        const firstDataColumn = dataRef.current.columns.find(
          (col) => col.name !== submittedKeyHeader
        );
        if (firstDataColumn) {
          setPendingTarget({
            entityKind,
            columnName: firstDataColumn.name,
            groupName: firstDataColumn.group,
            columnUnits: firstDataColumn.units
          });
        }
      }

      dataRef.current = null;
      form.resetFields();
      setShowModal(false);
      setFileList([]);
      resetState();
      getCategoryListWithUnit(entityKind);
      dispatch(getNormalizationItems());
      setColumnUploadedSignal((prev) => prev + 1);
    },
    (error) => {
      const errorMessage =
        error.response?.status === HttpStatusCode.PayloadTooLarge
          ? "The file exceeds the 80 MB limit. Please reduce the file size and try again."
          : extractAxiosErrorMessage(error);
      setError(errorMessage);
      Sentry.captureException(error);
    }
  );

  const uploadMutation = useUploadCustomColumnFileMutation(
    keyHeaderRef,
    entityKind,
    (data) => {
      // Validate that the file yielded something to work with. A file whose only columns
      // are headerless still has data the user can keep by naming them, so it must open the
      // naming step rather than error out — only a response with no named columns AND no
      // headerless columns is a genuine "no data" case.
      const hasNamedColumns = Boolean(data.columns && data.columns.length > 0);
      const hasUnnamedColumns = (data.unnamedColumns?.length ?? 0) > 0;
      if (!hasNamedColumns && !hasUnnamedColumns) {
        setError("No data found. Please make sure that your columns are populated.");
        const error = new Error("No columns found in file");
        uploadCallbackRef.current.onError(error);
        throw error;
      }

      const selectedKeyHeader = data.uwiHeaderName || "UWI";

      const allAvailableColumns = [selectedKeyHeader, ...data.columns.map((c) => c.name)];
      const keyHeaderOptions = [...new Set(allAvailableColumns)];
      setAvailableKeyHeaders(keyHeaderOptions);

      const filteredDataColumns = data.columns.filter((c) => {
        if (defaultUwiHeaders.includes(c.name)) {
          return c.name === selectedKeyHeader;
        }
        return true;
      });

      const nonKeyColumns = filteredDataColumns.map((c) => ({
        title: c.name,
        width: PREVIEW_COLUMN_WIDTH,
        group: c.group
      }));

      const keyHeaderColumn = { title: selectedKeyHeader, width: KEY_COLUMN_WIDTH };
      const cols = [keyHeaderColumn, ...nonKeyColumns];
      dataRef.current = { ...data, columns: filteredDataColumns };
      // Keep the raw (pre-rename) flagged set for the error count; dataRef's copy gets
      // remapped to edited names later for Review header tinting.
      uploadErrorColumnsRef.current = data.columnsWithErrors ?? [];
      setColumns(cols);
      keyHeaderRef.current = selectedKeyHeader;

      if (renameReuploadPendingRef.current) {
        // This response is the result of a naming-triggered rename re-upload. Verify each
        // entered name actually landed before treating the remaining unnamed indices as
        // the user's explicit drops — a rewritten header cell can miss (e.g. a stale
        // workbook dimension record), which would otherwise silently drop a column the
        // user asked to keep.
        const attemptedNames = renameReuploadNamesRef.current ?? {};
        const stillUnnamed = new Set(data.unnamedColumns ?? []);
        const everyNameLanded = Object.values(attemptedNames).every((name) =>
          data.columns.some((col) => col.name === name)
        );
        const anyAttemptedIndexStillUnnamed = Object.keys(attemptedNames).some(
          (indexStr) => stillUnnamed.has(Number(indexStr))
        );

        renameReuploadPendingRef.current = false;
        renameReuploadNamesRef.current = null;

        if (!everyNameLanded || anyAttemptedIndexStillUnnamed) {
          setError("The entered column name(s) could not be applied. Please try again.");
          setColumnNamingResolved(false);
          // The attempted names did not stick — drop the drafts so the still-unnamed columns
          // are counted again in the naming banner and the user re-enters them.
          setColumnNameDrafts({});
        } else {
          setColumnNamingResolved(true);
          setColumnNameDrafts({});
        }
      } else if (namingReevalSuppressedRef.current) {
        // Internal refresh of the file whose columns were already named — the user's earlier naming decision
        // (name some, drop the rest) stands.
        namingReevalSuppressedRef.current = false;
        setColumnNamingResolved(true);
        setColumnNameDrafts({});
      } else {
        // Any other new response (fresh file, key-header change) re-evaluates the naming step.
        setColumnNamingResolved((data.unnamedColumns?.length ?? 0) === 0);
        setColumnNameDrafts({});
      }

      uploadCallbackRef.current.onSuccess();
    }
  );

  const downloadMutation = useDownloadTemplateMutation(
    entityKind,
    filterId,
    facilityFilterId,
    setError
  );

  // Data-bearing/empty classification and sample values read straight from the response's
  // rawDataColumns — no client-side file parsing for naming-step display.
  const unnamedColumnIndices = dataRef.current
    ? deriveUnnamedColumnItems(dataRef.current)
    : [];
  // Named columns whose header is a duplicate — an exact repeat, or the backend's
  // "<base> (N)" rename of a duplicate source header. These are fixed right in the grid,
  // exactly like a headerless column: click the red header, type a unique name, re-upload.
  const namedRawHeaders = (dataRef.current?.rawDataColumns ?? [])
    .map((rc, index) => ({
      index,
      name: (rc.headerName ?? "").trim(),
      isKey: rc.isKeyHeader
    }))
    .filter((c) => !c.isKey && c.name.length > 0);
  // Effective (draft-aware) name each named column would submit as right now.
  const effectiveHeaderNames = namedRawHeaders.map(
    (c) => (columnNameDrafts[c.index] ?? "").trim() || c.name
  );
  // Every column that could collide, by effective name — named columns (draft or current
  // header) plus any name typed into a headerless column. A blank headerless draft can't
  // collide (it just drops), so it's excluded.
  const conflictCandidates = [
    ...namedRawHeaders.map((c) => ({
      index: c.index,
      name: (columnNameDrafts[c.index] ?? "").trim() || c.name
    })),
    ...unnamedColumnIndices.map((index) => ({
      index,
      name: (columnNameDrafts[index] ?? "").trim()
    }))
  ].filter((c) => c.name.length > 0);
  // Cells currently in a name conflict given the drafts — BOTH sides of the collision,
  // including a headerless column whose typed name now duplicates another. Tinted red; clears
  // live as the user types a unique name.
  const flaggedDuplicateIndexSet = new Set(
    findConflictingNameIndices(conflictCandidates.map((c) => c.name)).map(
      (i) => conflictCandidates[i].index
    )
  );
  // Duplicates present in the uploaded data itself (draft-independent). This keeps the rename
  // mode active and the cells clickable until the fix is actually applied (re-uploaded), so a
  // pending draft can never be skipped.
  const uploadedDuplicateIndexSet = new Set(
    findConflictingNameIndices(namedRawHeaders.map((c) => c.name)).map(
      (i) => namedRawHeaders[i].index
    )
  );

  const unnamedNamingActive = !columnNamingResolved && unnamedColumnIndices.length > 0;
  // The grid's "fix these headers" mode covers headerless AND duplicate-named columns; both
  // are renamed in place before the import can move on.
  const columnNamingActive = unnamedNamingActive || uploadedDuplicateIndexSet.size > 0;
  const renamableColumnIndices = Array.from(
    new Set([...unnamedColumnIndices, ...uploadedDuplicateIndexSet])
  ).sort((a, b) => a - b);

  function updateHeaderNameEditing(next: typeof headerNameEditing) {
    headerNameEditingRef.current = next;
    setHeaderNameEditing(next);
  }

  const existingColumnNames = dataRef.current
    ? collectExistingColumnNames(dataRef.current)
    : [];
  const columnNameErrors: Record<number, Nullable<string>> = {};
  renamableColumnIndices.forEach((index) => {
    columnNameErrors[index] = getColumnNameError(
      columnNameDrafts[index] ?? "",
      index,
      columnNameDrafts,
      existingColumnNames
    );
  });
  const hasColumnNameError = renamableColumnIndices.some((index) =>
    Boolean(columnNameErrors[index])
  );
  // A headerless column with a blank draft is simply dropped; a duplicate must be renamed so
  // no collision remains. Draft-aware (via effectiveHeaderNames), so it clears live as the
  // user types a unique name — renaming one side of a pair resolves both.
  const unresolvedDuplicateHeaderCount = flaggedDuplicateIndexSet.size;
  const hasUnresolvedDuplicateHeader = unresolvedDuplicateHeaderCount > 0;
  const enteredColumnNames: Record<number, string> = {};
  renamableColumnIndices.forEach((index) => {
    const trimmed = (columnNameDrafts[index] ?? "").trim();
    if (trimmed) {
      enteredColumnNames[index] = trimmed;
    }
  });
  const enteredColumnNameCount = Object.keys(enteredColumnNames).length;
  // Headerless columns still awaiting a name (blank draft). Draft-aware, so the "N columns
  // have no header" panel row ticks down live as the user names each one.
  const unnamedPendingCount = unnamedColumnIndices.filter(
    (index) => (columnNameDrafts[index] ?? "").trim().length === 0
  ).length;

  const disabled =
    fileList.length === 0 || uploadMutation.isLoading || columnNamingActive;
  const allSelected = selectedColumns.length === columns.length - 1;
  const hasAllInvalidUWI = dataRef.current?.uwis.every(
    (uwi) =>
      (entityKind === EntityKind.Well && uwi === "Invalid UWI") ||
      (entityKind === EntityKind.Facility && uwi === "Invalid Facility ID") ||
      uwi === undefined
  );
  const invalidUwiCount =
    dataRef.current?.uwis.filter(
      (uwi) =>
        (entityKind === EntityKind.Well && uwi === "Invalid UWI") ||
        (entityKind === EntityKind.Facility && uwi === "Invalid Facility ID") ||
        uwi === undefined
    ).length ?? 0;
  // Column names the user is actually keeping (null = all included). Used to drop
  // de-selected columns from the error count and the error-row filter, so removing an
  // error-bearing column updates the issues panel.
  const includedColumnNames = useMemo<Nullable<Set<string>>>(() => {
    if (selectedColumns.length === 0) return null;
    const names = new Set<string>();
    (dataRef.current?.columns ?? []).forEach((c) => {
      const units = c.units?.trim();
      const nameWithUnits = units ? `${c.name} (${units})` : c.name;
      if (selectedColumns.includes(c.name) || selectedColumns.includes(nameWithUnits)) {
        names.add(c.name);
      }
    });
    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, dataRef.current?.columns]);
  // On the upload step the de-selection set is live; on later steps column names are edited
  // and no longer match raw headers, so use the set frozen when leaving the upload step.
  const activeIncludedColumnNames =
    currentStep === 0 ? includedColumnNames : includedColumnNamesRef.current;
  // Count against the raw flagged set (rawDataColumns headers never get renamed), so the
  // count survives an Edit-Fields rename of a flagged column.
  const errorCountData = dataRef.current
    ? { ...dataRef.current, columnsWithErrors: uploadErrorColumnsRef.current }
    : dataRef.current;
  const errorCellCount = countErrorCells(errorCountData, activeIncludedColumnNames);

  // Column names that will be submitted must be unique. On the upload step that's the
  // draft-aware named columns plus any names typed for a headerless column; on later steps
  // it's the edited column set. Duplicates surface as a blocking red panel row and gate
  // Confirm Edits / Submit — the upload itself is never rejected, so the user can go fix them.
  const submittedNameList =
    currentStep >= 1
      ? tempColumns.map((col) => (col.name ?? "").trim())
      : [
          ...effectiveHeaderNames,
          ...unnamedColumnIndices.map((index) => (columnNameDrafts[index] ?? "").trim())
        ];
  const submittedColumnNames = submittedNameList.filter(
    (name) => name.length > 0 && name !== keyHeaderRef.current
  );
  // Reported as a count of duplicate *pairs* (groups that collapse to the same base name), so
  // one pair of columns reads as "1", not "2". Draft-aware — clears live as the user renames.
  const duplicatePairCount = countDuplicateGroups(submittedColumnNames);
  const hasDuplicateColumnNames = duplicatePairCount > 0;
  const duplicateNameCount = duplicatePairCount;
  // Import issues that are acceptable-but-worth-confirming still remain at submit time
  // (error cells folded to "n/a", rows with an unreadable key that will be skipped).
  // Duplicate names are a hard blocker instead, so they never reach this gate. Used to put
  // the final Submit behind an "Are you sure?" confirmation — the point of no return, and
  // the last step where the issues panel is no longer shown.
  const hasPanelIssues =
    errorCellCount > 0 || invalidUwiCount > 0 || duplicateNameCount > 0;
  const submitConfirmTitle = "This import still has warnings. Submit anyway?";

  const newFieldRequestObserver = useSelector(
    (state: RootState) => state.app.newFieldRequestObserver
  );

  // Subscribe to the newFieldRequestObserver to open the modal when a new field is requested
  // and set the entity kind based on the tab selected
  useEffect(() => {
    if (newFieldRequestObserver && !sub) {
      sub = newFieldRequestObserver.subscribe((payload: CustomFieldPayload) => {
        if (payload.type !== "Excel" || payload.isEdit) {
          return;
        }
        isOrgRef.current = payload.isOrg;
        projectIdRef.current = payload.projectId;
        groupNameDefaultRef.current = dayjs().format("YYYY-MM-DD");
        setGroupName(groupNameDefaultRef.current);
        setShowModal(true);
        setEntityKind(payload.entityKind);
        if (payload.fieldGroup) {
          setGroupName(payload.fieldGroup);
        }
      });
    }
    return () => {
      if (sub) {
        sub.unsubscribe();
        sub = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showModal) {
        e.stopPropagation();
        // While the header-naming overlay is open, Escape cancels the edit (reverting
        // the draft) instead of closing the wizard.
        const editing = headerNameEditingRef.current;
        if (editing) {
          setColumnNameDrafts((prev) => ({
            ...prev,
            [editing.rawIndex]: editing.initialDraft
          }));
          headerNameEditingRef.current = null;
          setHeaderNameEditing(null);
          return;
        }
        onClose();
      }
    };

    if (showModal) {
      document.addEventListener("keydown", handleEscape, true);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [showModal]);

  // Filter columns displayed in the preview table based on selected columns
  const filteredColumns = useMemo(() => {
    const styledColumns = getStyledColumns(
      columns,
      dataRef.current?.columnsWithErrors ?? [],
      dataRef.current?.columns ?? []
    );
    if (selectedColumns.length === 0) return styledColumns;
    return styledColumns.filter((col) => {
      const units = dataRef.current?.columns
        .find((c) => c.name === col.title)
        ?.units?.trim();
      const nameWithUnits = units ? `${col.title} (${units})` : col.title; // Append units if they exist
      return (
        col.title === keyHeaderRef.current ||
        selectedColumns.includes(col.title) ||
        selectedColumns.includes(nameWithUnits)
      );
    });
  }, [
    selectedColumns,
    columns,
    keyHeaderRef.current,
    dataRef.current?.columnsWithErrors
  ]);

  // While the header-naming step is active, the raw grid additionally shows the data-bearing
  // unnamed columns at their true positions so their headers can be named in place.
  const rawPreviewOrder = useMemo(
    () =>
      dataRef.current
        ? getRawPreviewColumnOrder(
            dataRef.current,
            selectedColumns,
            unnamedNamingActive ? unnamedColumnIndices : null
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unnamedNamingActive, selectedColumns, columns, unnamedColumnIndices.join(",")]
  );

  const rawGridColumns = useMemo(() => {
    if (!columnNamingActive) return filteredColumns;
    // Every non-key column (named, headerless, duplicate) renders at one uniform width.
    return [
      { title: keyHeaderRef.current, width: KEY_COLUMN_WIDTH },
      ...rawPreviewOrder.map((meta) =>
        meta.isUnnamed
          ? { title: "", id: `unnamed-${meta.rawIndex}`, width: PREVIEW_COLUMN_WIDTH }
          : { title: meta.headerName, width: PREVIEW_COLUMN_WIDTH }
      )
    ];
  }, [columnNamingActive, filteredColumns, rawPreviewOrder]);

  function resolveRawGridRow(row: number): number {
    if (filteredRawRowIndices && filteredRawRowIndices.length > 0) {
      return filteredRawRowIndices[row] ?? row;
    }
    return row;
  }

  // Raw-grid cells routed through the naming-step awareness: while the naming step is active, the
  // header cell of an unnamed column shows the current draft name (or a "Click to name"
  // affordance) instead of its blank wire value.
  function getRawPreviewCell(cell: Item): GridCell {
    const base = getRawData(
      cell,
      dataRef,
      selectedColumns,
      filteredRawRowIndices,
      entityKind,
      rawPreviewOrder,
      showActualValues
    );
    if (!columnNamingActive) return base;
    const [col, row] = cell;
    const meta = rawPreviewOrder[col - 1];
    if (col === 0 || !meta) return base;
    if (resolveRawGridRow(row) !== (dataRef.current?.headerRowIndex ?? 0)) return base;
    const draft = (columnNameDrafts[meta.rawIndex] ?? "").trim();
    // U+270E renders as a monochrome glyph (not a color emoji), so the pencil takes the
    // cell's text color and reads as the "click to name/rename" affordance.
    if (meta.isUnnamed) {
      // Orange "click to name" affordance — but if the typed name now duplicates another
      // column, the cell flips to the same soft red as any other duplicate.
      const isDuplicate = flaggedDuplicateIndexSet.has(meta.rawIndex);
      return {
        ...base,
        data: draft,
        // Keep the pencil beside the name after it's typed, so it still reads as editable.
        displayData: draft ? `✎ ${draft}` : "✎ Click to name",
        themeOverride: {
          bgCell: isDuplicate ? "#fff1f0" : "#fff2e0",
          textDark: isDuplicate ? "#cf1322" : draft ? "#000000" : "#d46b08",
          baseFontStyle: "bold 13px"
        }
      } as GridCell;
    }
    if (uploadedDuplicateIndexSet.has(meta.rawIndex)) {
      // Duplicate/renamed header — pencil affordance, showing the new draft or the current
      // name. Clicking opens the same in-place rename box as a headerless column. Both sides
      // of a live collision are tinted soft red; a side that's been resolved (unique draft)
      // drops back to the normal header colour while staying editable until re-upload.
      const isFlagged = flaggedDuplicateIndexSet.has(meta.rawIndex);
      return {
        ...base,
        data: draft,
        // Show the plain base name (drop the backend's confusing " (N)" suffix) until the
        // user types their own replacement.
        displayData: `✎ ${draft || stripDedupSuffix(meta.headerName)}`,
        themeOverride: {
          bgCell: isFlagged ? "#fff1f0" : "#f7f7f8",
          textDark: isFlagged ? "#cf1322" : "#000000",
          baseFontStyle: "bold 13px"
        }
      } as GridCell;
    }
    return base;
  }

  function handleRawGridCellClicked(cell: Item, event: CellClickedEventArgs) {
    if (!columnNamingActive) return;
    const [col, row] = cell;
    if (col === 0) return;
    const meta = rawPreviewOrder[col - 1];
    if (!meta) return;
    if (!meta.isUnnamed && !uploadedDuplicateIndexSet.has(meta.rawIndex)) return;
    if (resolveRawGridRow(row) !== (dataRef.current?.headerRowIndex ?? 0)) return;
    const containerRect = gridContainerRef.current?.getBoundingClientRect();
    const bounds = event?.bounds;
    updateHeaderNameEditing({
      rawIndex: meta.rawIndex,
      initialDraft: columnNameDrafts[meta.rawIndex] ?? "",
      bounds:
        bounds && containerRect
          ? {
              x: bounds.x - containerRect.left,
              y: bounds.y - containerRect.top,
              width: bounds.width,
              height: bounds.height
            }
          : { x: 0, y: 0, width: 150, height: 34 }
    });
  }

  // Filter rows to only those matching the active preview filters (invalid keys and/or
  // error cells). The two filters compose as a union.
  const {
    filteredRawRowIndices,
    filteredUwiRowIndices,
    effectiveNumRows,
    totalRows,
    previewCount
  } = useMemo(() => {
    const rawTotalRows = dataRef.current?.rawDataColumns?.[0]?.data?.length || 0;
    const headerRowIndex = dataRef.current?.headerRowIndex ?? 0;
    const headerCount = headerRowIndex + 1;
    const totalDataRows = Math.max(0, rawTotalRows - headerCount);

    const anyFilterActive = showInvalidUwisOnly || showErrorRowsOnly;
    if (!anyFilterActive || !dataRef.current?.uwis) {
      return {
        filteredRawRowIndices: null,
        filteredUwiRowIndices: null,
        effectiveNumRows: Math.min(rawTotalRows, headerCount + MAX_PREVIEW_ROWS),
        totalRows: totalDataRows,
        previewCount: Math.min(totalDataRows, MAX_PREVIEW_ROWS)
      };
    }

    // Invalid-key data rows (0-based from the UWI array).
    const invalidUwiIndices = showInvalidUwisOnly
      ? dataRef.current.uwis
          .map((uwi, index) => ({
            isInvalid:
              (entityKind === EntityKind.Well && uwi === "Invalid UWI") ||
              (entityKind === EntityKind.Facility && uwi === "Invalid Facility ID") ||
              uwi === undefined,
            originalIndex: index
          }))
          .filter(({ isInvalid }) => isInvalid)
          .map(({ originalIndex }) => originalIndex)
      : [];

    // Rows with a genuine Excel error cell (same 0-based data-row space as the UWI array),
    // restricted to the columns the user is keeping. Uses the raw flagged set so it matches
    // rawDataColumns headers regardless of later renames.
    const errorRowIndices = showErrorRowsOnly
      ? errorDataRowIndices(
          dataRef.current
            ? { ...dataRef.current, columnsWithErrors: uploadErrorColumnsRef.current }
            : dataRef.current,
          includedColumnNames
        )
      : [];

    // Union of the active filters, ascending, so both toggles apply together.
    const matchedIndices = Array.from(
      new Set<number>([...invalidUwiIndices, ...errorRowIndices])
    ).sort((a, b) => a - b);

    // For raw data: include the header rows + matched data rows (with header offset).
    const headerIndices = Array.from({ length: headerRowIndex + 1 }, (_, i) => i);
    const rawRowIndices = [
      ...headerIndices,
      ...matchedIndices.map((idx) => idx + headerRowIndex + 1)
    ];

    return {
      filteredRawRowIndices: rawRowIndices,
      filteredUwiRowIndices: matchedIndices,
      effectiveNumRows: Math.min(rawRowIndices.length, MAX_PREVIEW_ROWS),
      totalRows: matchedIndices.length,
      previewCount: Math.min(matchedIndices.length, MAX_PREVIEW_ROWS)
    };
  }, [
    showInvalidUwisOnly,
    showErrorRowsOnly,
    includedColumnNames,
    dataRef.current?.uwis,
    dataRef.current?.headerRowIndex,
    entityKind
  ]);

  function onSubmit() {
    try {
      form
        .validateFields()
        .then(() => {
          const data = dataRef.current;
          if (!data) {
            return;
          }
          //remove any existing errors
          setError("");
          // Send an explicit per-column order — without it every column is stored
          // with order 0 and the group-by tab falls back to alphabetical.
          postMutation.mutate(
            data.columns.map((column, index) => ({
              ...column,
              withinSubGroupOrder: index
            }))
          );
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((info: any) => {
          const errorMessage =
            info instanceof Error
              ? info.message
              : typeof info === "string"
              ? info
              : typeof info === "object" && info?.message
              ? info.message
              : "Validation failed.";
          setError(errorMessage);
        });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  function resetState() {
    setCurrentStep(0);
    setFileList([]);
    setEditsConfirmed(false);
    setColumns(initialColumns);
    setSelectedColumns([]);
    groupNameDefaultRef.current = dayjs().format("YYYY-MM-DD");
    setGroupName(groupNameDefaultRef.current);
    setError("");
    setShowInvalidUwisOnly(false);
    setShowErrorRowsOnly(false);
    setShowActualValues(false);
    setAvailableKeyHeaders([]);
    setColumnNameDrafts({});
    updateHeaderNameEditing(null);
    setColumnNamingResolved(true);
    renameReuploadPendingRef.current = false;
    renameReuploadNamesRef.current = null;
    namingReevalSuppressedRef.current = false;
    uploadErrorColumnsRef.current = [];
    includedColumnNamesRef.current = null;
    setAutoGroupByChecked(true);
    dataRef.current = null;
    keyHeaderRef.current = "Entity";
  }

  function clearColumnEdits() {
    moveToPreviousWizardStep();
    setSelectedColumns([]);
    // Keep the typed group name — going back to re-select columns must not discard it.
    setEditsConfirmed(false);
    // Same file, naming already resolved — the refresh must not reopen the naming step.
    namingReevalSuppressedRef.current = true;
    handleCustomUploadRequest(uploadCallbackRef.current);
  }

  // Close the modal
  function onClose() {
    resetState();
    setShowModal(false);
  }

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx, .xls, .csv",
    onRemove: handleFileRemove,
    customRequest: handleCustomUploadRequest,
    showUploadList: {
      showRemoveIcon: !uploadMutation.isLoading
    }
  };

  // Remove the uploaded file and reset the wizard
  function handleFileRemove() {
    resetState();
  }

  async function handleCustomUploadRequest(callback) {
    try {
      setError("");
      setFileList([callback.file]);
      uploadCallbackRef.current = callback;
      await uploadMutation.mutateAsync(callback.file);
      callback.onSuccess();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "An unexpected error occurred.";
      setError(errorMessage);
      Sentry.captureException(error);
      // A failed internal re-upload (Previous-from-Edit-Fields, key-header change) never
      // reaches the success handler that would clear this flag, so a later unrelated upload
      // would be mistaken for an already-named refresh and its unnamed columns silently
      // dropped. Clear it on every failure so the next response is re-evaluated.
      namingReevalSuppressedRef.current = false;
      callback.onError(error);
    } finally {
      uploadMutation.reset();
    }
  }

  const moveToNextWizardStep = () => {
    if (currentStep < steps.length - 1) {
      setTempColumns(
        selectedColumns.length > 0
          ? dataRef.current.columns.filter((col) => selectedColumns.includes(col.name))
          : dataRef.current.columns
      );
      // Leaving the upload step: freeze which columns are kept (for the error count on later
      // steps, where names are edited and no longer match the raw headers) and drop the
      // raw-preview-only "show error rows" filter, which has no toggle on later steps.
      if (currentStep === 0) {
        includedColumnNamesRef.current = includedColumnNames;
        setShowErrorRowsOnly(false);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const moveToPreviousWizardStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Confirm the column edits and move to the next step
  // Update the column titles to include units if provided for columns and selectedColumns (DISPLAY DATA)
  // not for dataRef.current.columns as units will be applied after the columns are posted (ACTUAL DATA)
  const confirmColumnEdits = () => {
    // The stored group name always equals a value visible in the Group Name input (the
    // typed value, or the date placeholder shown when the input is cleared).
    const effectiveGroupName = groupName.trim() || groupNameDefaultRef.current;
    setColumns([
      {
        title: dataRef.current.uwiHeaderName,
        width: 150
      },
      ...tempColumns.map((col, index) => {
        const units = col.units?.trim();
        const name =
          col.name?.trim() || dataRef.current.columns[index]?.name || "Untitled";
        return {
          title: units ? `${name} (${units})` : name, // Append units if they were provided
          width: 100,
          group: effectiveGroupName
        };
      })
    ]);

    // Remap flagged names through the rename (original -> edited) so Review-step
    // highlighting survives — this preserves identity, it never re-derives error
    // status from cells. tempColumns is built from the same selectedColumns-filtered
    // subset of dataRef.current.columns (see moveToNextWizardStep), so the flagged
    // name must be located within that same subset, not the full column list, or the
    // remap lands on the wrong column whenever a subset was selected. A second pass
    // through this step sees selectedColumns holding unit-suffixed titles, so match
    // both the bare name and the "name (units)" form to keep the subset aligned.
    const isSelectedColumn = (col: ColumnModel) => {
      const units = col.units?.trim();
      return (
        selectedColumns.includes(col.name) ||
        (Boolean(units) && selectedColumns.includes(`${col.name} (${units})`))
      );
    };
    const tempColumnsSource =
      selectedColumns.length > 0
        ? dataRef.current.columns.filter(isSelectedColumn)
        : dataRef.current.columns;
    const remappedErrorColumns = (dataRef.current.columnsWithErrors ?? []).map(
      (flaggedName) => {
        const index = tempColumnsSource.findIndex((col) => col.name === flaggedName);
        if (index === -1) return flaggedName;
        return (
          tempColumns[index]?.name?.trim() || tempColumnsSource[index]?.name || "Untitled"
        );
      }
    );

    // Ensure groupName is set in dataRef columns before submission
    dataRef.current.columns = tempColumns.map((col) => ({
      ...col,
      group: effectiveGroupName
    }));
    dataRef.current.columnsWithErrors = remappedErrorColumns;

    const selectedColumnsWithUnits = tempColumns.map((col, index) => {
      const units = col.units?.trim();
      const name = col.name?.trim() || dataRef.current.columns[index]?.name || "Untitled";
      return units ? `${name} (${units})` : name; // Append units if they were provided
    });
    setSelectedColumns(selectedColumnsWithUnits);
    setCurrentStep(currentStep + 1);
  };

  // Update the column details in the tempColumns state
  const handleTempColumnChange = (index: number, field: string, value: string) => {
    const updatedTempColumns = [...tempColumns];
    updatedTempColumns[index] = {
      ...updatedTempColumns[index],
      [field]: value?.trim() || dataRef.current.columns[index]?.[field] || ""
    };
    setTempColumns(updatedTempColumns);
  };

  const updateGroupName = (value: string) => {
    // Only track the input value here. The group is stamped onto the columns once, in
    // confirmColumnEdits (and re-derived at submit), so rewriting every column on each
    // keystroke was dead work that confirm always overwrote.
    setGroupName(value);
  };

  // Re-upload the file with the keyHeaderRef column as the new primary key column (only if the file has already uploaded)
  function updateKeyHeader(value: string) {
    if (fileList.length > 0 && value) {
      keyHeaderRef.current = value;
      // The key header can only change after the naming step is already resolved (the
      // control is locked while it is active), so the re-upload must keep that decision —
      // it must not drop the user back into naming the columns they already handled.
      namingReevalSuppressedRef.current = true;
      handleCustomUploadRequest(uploadCallbackRef.current);
    }
  }

  function handleColumnNameDraftChange(index: number, value: string) {
    setColumnNameDrafts((prev) => ({ ...prev, [index]: value }));
  }

  // Proceed with zero valid names resolves the naming step locally (backend guarantees unnamed
  // columns are never imported, so no network call is needed). Proceed with at least one
  // valid name rewrites the client-held file's header cells and re-uploads it through the
  // existing upload path so the named column gets server-side normalization rather than
  // being constructed client-side from raw data.
  async function handleApplyColumnNames(names: Record<number, string>) {
    updateHeaderNameEditing(null);
    if (Object.keys(names).length === 0) {
      setColumnNamingResolved(true);
      setColumnNameDrafts({});
      return;
    }

    const currentFile = uploadCallbackRef.current.file;
    if (!currentFile || !dataRef.current) {
      setColumnNamingResolved(true);
      setColumnNameDrafts({});
      return;
    }

    try {
      const renamedFile = await renameWorkbookHeaders(
        currentFile,
        dataRef.current.headerRowIndex,
        names
      );
      // A rewritten File has no antd uid of its own; carry the original file's uid
      // (falling back to a generated one) so Ant Upload's internal bookkeeping still
      // treats this as the same list entry rather than an untracked file.
      const existingUid = (currentFile as unknown as UploadFile).uid;
      Object.assign(renamedFile, { uid: existingUid ?? `header-rename-${Date.now()}` });

      // Carry the previous callback's own onError so a failed re-upload still reaches
      // Ant Upload's bookkeeping, in addition to clearing the pending flag below.
      const previousOnError = uploadCallbackRef.current.onError;
      const renameUploadCallback = {
        ...uploadCallbackRef.current,
        file: renamedFile,
        onError: (err) => {
          // A failed re-upload never reaches the onSuccess handler that would otherwise
          // clear this flag, so a later, unrelated response would be mistaken for a
          // rename re-upload result and its own unnamed columns silently dropped.
          renameReuploadPendingRef.current = false;
          renameReuploadNamesRef.current = null;
          // Drop the drafts so the still-unnamed columns are counted again in the banner.
          setColumnNameDrafts({});
          previousOnError(err);
        }
      };
      uploadCallbackRef.current = renameUploadCallback;
      setFileList([renamedFile as unknown as UploadFile]);
      renameReuploadNamesRef.current = names;
      renameReuploadPendingRef.current = true;
      await handleCustomUploadRequest(renameUploadCallback);
    } catch (err) {
      setError("Unable to rename the selected column(s). Please try again.");
      // Drop the drafts so the still-unnamed columns are counted again in the banner.
      setColumnNameDrafts({});
      // eslint-disable-next-line no-console
      console.error(err);
      Sentry.captureException(err);
    }
  }

  // Wizard steps
  const steps = [
    {
      title: "Upload Custom Fields",
      content: (
        <>
          <StepContainer $collapsed={fileList.length > 0}>
            {/* Once a file is in, the drop area collapses to a compact bar (file chip +
                small template button) so the preview table gets the vertical space; the
                chip's remove icon resets back to the full dropzone. */}
            <Card>
              {/* Hidden when collapsed via the `.ant-upload-drag` rule in $collapsed CSS. */}
              <Dragger {...uploadProps} fileList={fileList} maxCount={1}>
                <Spin
                  spinning={uploadMutation.isLoading}
                  indicator={<IconSpinner></IconSpinner>}>
                  <div className="ant-upload-text">
                    <UploadMsgContainer>
                      <Icon path={mdiFileUpload} size={1.8} />
                      <p>Click or drag custom columns file to this area to upload.</p>
                    </UploadMsgContainer>
                  </div>
                </Spin>
              </Dragger>
            </Card>
            <DownloadTemplateButton
              type="primary"
              key="cancel"
              size={fileList.length > 0 ? "small" : "middle"}
              $collapsed={fileList.length > 0}
              loading={downloadMutation.isLoading}
              onClick={() => {
                downloadMutation.mutate();
              }}>
              <Icon path={mdiDownload} size={fileList.length > 0 ? 0.8 : 1.2} /> Download
              Excel Template (.xlsx)
            </DownloadTemplateButton>
          </StepContainer>

          <Title>
            Select the primary key column and the other columns you would like to include.
          </Title>
          <ColumnSelectContainer>
            <FormGroup>
              <Label>Key Header</Label>
              <Tooltip
                title={columnNamingActive ? NAMING_LOCK_HINT : ""}
                overlayStyle={{ zIndex: 9999 }}>
                <LockableControl $locked={columnNamingActive}>
                  <Select
                    data-testid="uwi-column-select"
                    value={columns[0].title}
                    dropdownStyle={{ zIndex: 9999 }}
                    disabled={disabled}
                    style={{ width: "100%" }}
                    options={
                      availableKeyHeaders.length > 0
                        ? availableKeyHeaders.map((header) => {
                            return { value: header, label: header };
                          })
                        : [{ value: columns[0].title, label: columns[0].title }]
                    }
                    onChange={(value) => {
                      updateKeyHeader(value);
                    }}
                  />
                </LockableControl>
              </Tooltip>
            </FormGroup>

            <FormGroup>
              <Label>Group Name</Label>
              <Input
                data-testid="group-name-input"
                value={groupName}
                placeholder={groupNameDefaultRef.current}
                onChange={(e) => {
                  updateGroupName(e.target.value);
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Column Select</Label>
              <Tooltip
                title={columnNamingActive ? NAMING_LOCK_HINT : ""}
                overlayStyle={{ zIndex: 9999 }}>
                <LockableControl $locked={columnNamingActive}>
                  <Select
                    data-testid="column-select"
                    mode="multiple"
                    maxTagCount="responsive"
                    placeholder="Select columns (all will be selected if none are included)"
                    value={selectedColumns}
                    dropdownStyle={{ zIndex: 9999 }}
                    disabled={disabled}
                    style={{ width: "100%" }}
                    options={columns.slice(1).map((c) => {
                      return { value: c.title, label: c.title };
                    })}
                    onChange={(value) => {
                      setSelectedColumns(value);
                    }}
                  />
                </LockableControl>
              </Tooltip>
            </FormGroup>

            <SelectAllButton
              type="primary"
              data-testid="select-all-button"
              onClick={() => {
                setSelectedColumns(
                  allSelected ? [] : columns.slice(1).map((c) => c.title)
                );
              }}
              disabled={disabled}>
              {allSelected ? "Clear Columns" : "Select All"}
            </SelectAllButton>
          </ColumnSelectContainer>

          <ImportIssuesPanel
            errorCellCount={errorCellCount}
            errorFilter={{
              active: showErrorRowsOnly,
              onToggle: () => setShowErrorRowsOnly((value) => !value)
            }}
            originalValues={{
              show: showActualValues,
              onToggle: setShowActualValues
            }}
            invalidUwiCount={invalidUwiCount}
            invalidFilter={{
              active: showInvalidUwisOnly,
              onToggle: () => setShowInvalidUwisOnly(!showInvalidUwisOnly)
            }}
            unnamedColumnCount={columnNamingActive ? unnamedPendingCount : 0}
            duplicateNameCount={duplicateNameCount}
            entityKind={entityKind}
          />
          <DataContainer>
            <GridContainer ref={gridContainerRef}>
              <DataEditor
                width="100%"
                height="100%"
                theme={GLIDE_GRID_THEME}
                getCellContent={(cell) => getRawPreviewCell(cell)}
                columns={rawGridColumns}
                rows={effectiveNumRows}
                headerHeight={0}
                onCellClicked={handleRawGridCellClicked}
                onVisibleRegionChanged={(range) => {
                  // Only dismiss the in-place rename overlay on an actual scroll (its
                  // anchored position would otherwise drift) — never on a re-render/resize
                  // (e.g. the issues panel shrinking as the user types), which would yank
                  // focus out of the input after a single keystroke.
                  const prev = lastVisibleRegionRef.current;
                  lastVisibleRegionRef.current = { x: range.x, y: range.y };
                  const scrolled = prev && (prev.x !== range.x || prev.y !== range.y);
                  if (headerNameEditingRef.current && scrolled) {
                    updateHeaderNameEditing(null);
                  }
                }}
                getCellsForSelection={(selection) => {
                  const result: GridCell[][] = [];
                  for (let y = selection.y; y < selection.y + selection.height; y++) {
                    const row: GridCell[] = [];
                    for (let x = selection.x; x < selection.x + selection.width; x++) {
                      row.push(getRawPreviewCell([x, y]));
                    }
                    result.push(row);
                  }
                  return result;
                }}
              />
              {headerNameEditing && (
                <HeaderNameOverlay
                  style={{
                    left: headerNameEditing.bounds.x,
                    top: headerNameEditing.bounds.y,
                    width: Math.max(150, headerNameEditing.bounds.width),
                    height: headerNameEditing.bounds.height
                  }}>
                  <Input
                    autoFocus
                    data-testid={`column-name-input-${headerNameEditing.rawIndex}`}
                    placeholder={
                      uploadedDuplicateIndexSet.has(headerNameEditing.rawIndex)
                        ? "Enter a unique name"
                        : "Enter a name to keep this column"
                    }
                    status={
                      columnNameErrors[headerNameEditing.rawIndex] ? "error" : undefined
                    }
                    value={columnNameDrafts[headerNameEditing.rawIndex] ?? ""}
                    onChange={(e) =>
                      handleColumnNameDraftChange(
                        headerNameEditing.rawIndex,
                        e.target.value
                      )
                    }
                    onPressEnter={() => updateHeaderNameEditing(null)}
                    onBlur={() => updateHeaderNameEditing(null)}
                  />
                  {/* No per-cell error text — the import issues panel already reports a
                      duplicate/blocking name; the red input border is the only in-cell cue. */}
                </HeaderNameOverlay>
              )}
            </GridContainer>
          </DataContainer>
        </>
      )
    },
    {
      title: "Edit Fields",
      content: (
        <DataContainer>
          {/* Message-only panel (no grid to filter here) so a duplicate name created by an
              edit — or one that arrived from the file — is surfaced on this step too. */}
          <ImportIssuesPanel
            errorCellCount={errorCellCount}
            invalidUwiCount={invalidUwiCount}
            unnamedColumnCount={0}
            duplicateNameCount={duplicateNameCount}
            entityKind={entityKind}
          />
          <Title>
            Edit column details -- If left blank, the column name will remain unchanged,
            and no units will be assigned.
          </Title>
          <EditFieldsListContainer data-testid="edit-fields-list">
            {filteredColumns
              .filter((col) => col.title !== keyHeaderRef.current)
              .map((col, index) => (
                <ColumnEditContainer key={index}>
                  <Form.Item>
                    {index === 0 && <Label>Column Name</Label>}
                    <Input
                      placeholder={col.title}
                      onChange={(e) => {
                        handleTempColumnChange(index, "name", e.target.value);
                      }}
                    />
                  </Form.Item>
                  <Form.Item>
                    {index === 0 && <Label>Units of Measure</Label>}
                    <Input
                      placeholder="Units"
                      onChange={(e) => {
                        handleTempColumnChange(index, "units", e.target.value);
                      }}
                    />
                  </Form.Item>
                </ColumnEditContainer>
              ))}
          </EditFieldsListContainer>
        </DataContainer>
      )
    },
    {
      title: "Review and Submit",
      content: (
        <DataContainer>
          <ImportIssuesPanel
            errorCellCount={errorCellCount}
            invalidUwiCount={invalidUwiCount}
            invalidFilter={{
              active: showInvalidUwisOnly,
              onToggle: () => setShowInvalidUwisOnly(!showInvalidUwisOnly)
            }}
            unnamedColumnCount={0}
            duplicateNameCount={duplicateNameCount}
            entityKind={entityKind}
          />
          <ReviewOptions>
            <Checkbox
              checked={autoGroupByChecked}
              onChange={(e) => setAutoGroupByChecked(e.target.checked)}>
              Group by first imported column after import
            </Checkbox>
          </ReviewOptions>
          <GridContainer>
            <DataEditor
              width="100%"
              height="100%"
              theme={GLIDE_GRID_THEME}
              getCellContent={(cell) =>
                getData(cell, dataRef, selectedColumns, filteredUwiRowIndices, entityKind)
              }
              columns={filteredColumns}
              rows={effectiveNumRows}
              getCellsForSelection={(selection) => {
                const result: GridCell[][] = [];
                for (let y = selection.y; y < selection.y + selection.height; y++) {
                  const row: GridCell[] = [];
                  for (let x = selection.x; x < selection.x + selection.width; x++) {
                    const cell = getData(
                      [x, y],
                      dataRef,
                      selectedColumns,
                      filteredUwiRowIndices,
                      entityKind
                    );
                    row.push(cell);
                  }
                  result.push(row);
                }
                return result;
              }}
            />
          </GridContainer>
        </DataContainer>
      )
    }
  ];

  return (
    <RootContainer>
      <ErrorBoundary>
        <ModalGlobalStyle />
        <Modal
          open={showModal}
          onOk={onSubmit}
          width={MODAL_WIDTH}
          wrapClassName={"user-defined-modal-root"}
          onCancel={onClose}
          closable={false}
          keyboard={false}
          zIndex={1101}
          footer={
            <ModalFooterWrapper>
              <FooterRow>
                <PreviewFooter>
                  <p>
                    {currentStep !== 1
                      ? getPreviewHeaderText(previewCount, totalRows)
                      : ""}
                  </p>
                  {error && error.length > 0 && (
                    <>
                      <FooterDivider>—</FooterDivider>
                      <ErrorWrapper>
                        <ErrorOutline /> {error}
                      </ErrorWrapper>
                    </>
                  )}
                </PreviewFooter>
                <FooterButtons>
                  <Button key="cancel" onClick={onClose}>
                    Cancel
                  </Button>
                  {currentStep > 0 &&
                    (currentStep === 1 && editsConfirmed ? (
                      <Popconfirm
                        title="Are you sure you want to go back? Unsaved edits will be lost."
                        onConfirm={() => {
                          clearColumnEdits();
                        }}
                        okText="Yes"
                        cancelText="No"
                        overlayStyle={{ zIndex: 9999 }}>
                        <Button key="previous" disabled={disabled}>
                          Previous
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        key="previous"
                        onClick={moveToPreviousWizardStep}
                        disabled={disabled}>
                        Previous
                      </Button>
                    ))}
                  {currentStep == 0 &&
                    (columnNamingActive ? (
                      <Tooltip
                        title={
                          hasColumnNameError || hasUnresolvedDuplicateHeader
                            ? NAMING_LOCK_HINT
                            : ""
                        }
                        overlayStyle={{ zIndex: 9999 }}>
                        <Button
                          key="apply-column-names"
                          type="primary"
                          data-testid="apply-column-names-button"
                          loading={uploadMutation.isLoading}
                          disabled={
                            hasColumnNameError ||
                            uploadMutation.isLoading ||
                            hasUnresolvedDuplicateHeader
                          }
                          onClick={() => handleApplyColumnNames(enteredColumnNames)}>
                          {uploadedDuplicateIndexSet.size > 0 ||
                          enteredColumnNameCount > 0
                            ? "Apply names"
                            : "Continue without naming"}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        key="next"
                        type="primary"
                        data-testid="next-button"
                        onClick={moveToNextWizardStep}
                        disabled={disabled || hasAllInvalidUWI}>
                        Next
                      </Button>
                    ))}
                  {currentStep == 1 && (
                    <Button
                      key="confirm edits"
                      type="primary"
                      data-testid="confirm-edits-button"
                      disabled={hasDuplicateColumnNames}
                      onClick={() => {
                        confirmColumnEdits();
                        setEditsConfirmed(true);
                      }}>
                      Confirm Edits
                    </Button>
                  )}
                  {currentStep === steps.length - 1 &&
                    (hasPanelIssues ? (
                      <Popconfirm
                        title={submitConfirmTitle}
                        onConfirm={onSubmit}
                        okText="Yes"
                        cancelText="No"
                        overlayStyle={{ zIndex: 9999 }}>
                        <Button
                          key="submit"
                          type="primary"
                          data-testid="submit-button"
                          loading={postMutation.isLoading}
                          disabled={hasDuplicateColumnNames}>
                          Submit
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        key="submit"
                        type="primary"
                        data-testid="submit-button"
                        loading={postMutation.isLoading}
                        disabled={hasDuplicateColumnNames}
                        onClick={onSubmit}>
                        Submit
                      </Button>
                    ))}
                </FooterButtons>
              </FooterRow>
            </ModalFooterWrapper>
          }>
          <StepsContainer>
            <Steps current={currentStep} size="small">
              {steps.map((step, index) => (
                <Steps.Step key={index} title={step.title} />
              ))}
            </Steps>
          </StepsContainer>
          <StepContentWrapper>{steps[currentStep].content}</StepContentWrapper>
        </Modal>
      </ErrorBoundary>
    </RootContainer>
  );
}

const RootContainer = styled.div`
  .data-grid-container {
    width: 100%;
  }
`;

const StepContainer = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  /* JSX order is [upload card, template button]; pre-upload the button renders on top. */
  flex-direction: column-reverse;
  gap: 5px;
  button {
    background: var(--color-text);
    border: var(--color-text);
  }

  /* With a file in, collapse to one compact row: file chip first, then the small
     template button. The drop area hides but stays mounted so the upload list (and its
     remove icon, which resets the wizard) keeps working. */
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      flex-direction: row;
      justify-content: flex-start;
      gap: 12px;

      .ant-upload-drag {
        display: none !important;
      }
      .ant-card-body {
        padding: 2px 8px;
      }
      .ant-upload-list {
        margin: 0;
        min-width: 240px;
      }
    `}

  .ant-modal-content {
    padding: 0;
  }

  .ant-upload-drag {
    border: 4px dashed #d9d9d9;
    padding: 24px;
    .ant-upload {
      padding: 0;
    }
    &:hover {
      border-color: var(--color-primary) !important;
    }
  }
  .ant-spin-nested-loading > div > .ant-spin {
    top: 60px;
  }
  .ant-upload-text {
    padding: 28px 24px;
  }
  p {
    line-height: 16pt;
  }
  .dvn-scroller {
    border: 1px solid lightgray;
  }
  .ant-upload-list-item-card-actions-btn {
    span {
      color: white;
    }
  }
`;
const ErrorWrapper = styled.div`
  color: red;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const DataContainer = styled.div`
  padding: 5px 0px 5px 0px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// Options bar above the Review-and-Submit grid (currently the auto group-by toggle).
const ReviewOptions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

// Height derives from the flex chain up to the modal body (no hardcoded height) —
// glide-data-grid v6 is virtualized and scrolls data overflow internally.
const GridContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const EditFieldsListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const UploadMsgContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StepContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ColumnSelectContainer = styled.div`
  display: grid;
  grid-template-columns: 170px 190px 1fr auto;
  gap: 10px;
  align-items: center;
  margin-top: 5px;
  width: 100%;
`;

const ColumnEditContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 5px;
  margin-bottom: -10px;
  align-items: center;
`;

const StepsContainer = styled.div`
  .ant-steps-item-icon > .ant-steps-icon {
    line-height: 1.8;
    vertical-align: top;
  }
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 4px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Wraps a disabled Select so its Tooltip still fires: a disabled control emits no hover
// events, so the hover target has to be this wrapper. Block + full width keeps the Select
// sized as it was as a direct flex child of FormGroup.
const LockableControl = styled.span<{ $locked?: boolean }>`
  display: block;
  width: 100%;
  cursor: ${({ $locked }) => ($locked ? "not-allowed" : "default")};
`;

const SelectAllButton = styled(Button)`
  margin-top: 24.5px; /* Adjust to align with other inputs */
`;

const DownloadTemplateButton = styled(Button)<{ $collapsed?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? "auto" : "510px")};
  margin-top: 5px;
  margin-bottom: ${({ $collapsed }) => ($collapsed ? "5px" : "10px")};
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
`;

const Title = styled.p`
  margin-top: 10px;
  margin-bottom: 5px;
`;

const HeaderNameOverlay = styled.div`
  position: absolute;
  z-index: 10;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;

  /* The input fills the overlay so it matches the header cell's bounds exactly. */
  .ant-input,
  .ant-input-affix-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const PreviewFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  p {
    margin: 0;
    white-space: nowrap;
    font-size: 14px;
  }

  /* antd offsets the checkbox box 0.2em below its label's baseline; flex-center the
     pair so the label sits on the same line as the preview text beside it. */
  .ant-checkbox-wrapper {
    display: inline-flex;
    align-items: center;

    .ant-checkbox {
      top: 0;
    }
  }
`;

const FooterDivider = styled.span`
  color: #a2aaad;
`;

const ModalFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
